import { IAbonement, IPlan, IPlanWithExtraParams } from '@/types/plan.interface'
import internalNamesByCategory from '../constants/internal-names-by-category.constant'
import EPlanCategory from '../enums/plan-category.enum'
import { EPeriod } from '../enums/period.enum'

const findCategoriesByInternalName = (internalName: string) => {
  const categories: IPlanWithExtraParams['extraParams']['categories'] = []

  for (const category in internalNamesByCategory) {
    if (internalNamesByCategory[category as EPlanCategory].includes(internalName)) {
      categories.push(category as IPlanWithExtraParams['extraParams']['categories'][number])
    }
  }

  return categories
}

const getStartAbonementByPlan = (plan: IPlan): IAbonement | null => {
  if (plan.defaultP1YAbonement && plan.defaultP1YAbonement.service.active)
    return plan.defaultP1YAbonement
  if (plan.abonementOnly)
    return plan.abonements.find((abonement) => abonement.service.active) || null
  return null
}

const hasFreeRuRfDomain = (plan: IPlan) => {
  return Boolean(
    plan.abonements.find(
      (abonement) =>
        abonement.service.active && !abonement.internal && abonement.extraParams?.freeRuRfDomain
    )
  )
}

const hasFreeSslCertificate = (plan: IPlan) => {
  return Boolean(plan.sslCertificateAllowed)
}

const getCheapestPriceOfMonthByPlan = (plan: IPlan) => {
  const abonements = plan.abonements.filter(
    (abonement) => !abonement.trial && abonement.service.active && !abonement.internal
  )

  const pricesOfMonth = abonements.map((abonement) =>
    Math.ceil(abonement.service.cost / abonement.approximatePeriodInMonths)
  )
  return pricesOfMonth.length ? Math.min(...pricesOfMonth) : plan.service.cost
}

const getCheapestPriceOfYearByPlan = (plan: IPlan) => {
  const abonements = plan.abonements.filter(
    (abonement) => !abonement.trial && abonement.service.active && !abonement.internal
  )

  const pricesOfMonth = abonements.map(
    (abonement) => (abonement.service.cost / abonement.approximatePeriodInMonths) * 12
  )
  return pricesOfMonth.length ? Math.min(...pricesOfMonth) : plan.service.cost * 12
}

const getPeriodsByPlan = (plan: IPlan): EPeriod[] => {
  let periods = plan.abonements
    .filter((abonement) => abonement.service.active && !abonement.internal)
    .map((abonement) => abonement.period)

  if (!plan.abonementOnly && plan.service)
    periods = [...periods, plan.service.paymentType === 'MONTH' ? EPeriod.P1M : EPeriod.P1D]
  if (plan.freeTrialAbonement) periods = [...periods, EPeriod.P7D]

  return periods
}

const getPlanWithExtraParams = (plan: IPlan): IPlanWithExtraParams => {
  const extraParams: IPlanWithExtraParams['extraParams'] = {
    categories: findCategoriesByInternalName(plan.internalName),
    startAbonement: getStartAbonementByPlan(plan),
    freeRuRfDomain: hasFreeRuRfDomain(plan),
    freeSslCertificate: hasFreeSslCertificate(plan),
    cheapestPricePerMonth: getCheapestPriceOfMonthByPlan(plan),
    cheapestPricePerYear: getCheapestPriceOfYearByPlan(plan),
    periods: getPeriodsByPlan(plan),
  }
  return {
    ...plan,
    extraParams,
  }
}

export default getPlanWithExtraParams
